import React, {useState, useEffect, useRef} from 'react';
import {useHistory} from "react-router-dom";
import {withTranslation} from 'react-i18next';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {Panel} from "primereact/panel";
import {InputText} from "primereact/inputtext";
import {Calendar} from "primereact/calendar";
import {TriStateCheckbox} from "primereact/tristatecheckbox";
import axios from "axios";
import moment from "moment";

const ProjectListComponent = (props) => {

  const {t, detailedSearchVisible, view, onDetailedSearchActiveChange} = props;
  const [localDetailedSearchVisible, setLocalDetailedSearchVisible] = useState(false);
  const isSearchView = view === 'search';
  const isAllProjectsView = props.allProjects || isSearchView;
  const isDetailedSearchOpen = detailedSearchVisible || (isSearchView && localDetailedSearchVisible);
  const history = useHistory();
  const [projects, setProjects] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeConfig, setTypeConfig] = useState(null);

  const { contractId, partnerId, hidePartnerColumn } = props;
  const [projectTypes, setProjectTypes] = useState([]);
  const [locations, setLocations] = useState([]);

  const [totalRecords, setTotalRecords] = useState(0);

  const urlVars = window.App.urlVars();
  const listContextKey = `${urlVars.from || ''}-${urlVars.project_type || ''}`;
  const storageKey = `projectListState-${listContextKey}`;
  const rowsStorageKey = `projectListRows-${listContextKey}`;
  const DEFAULT_SORT_FIELD = 'created_at';
  const DEFAULT_SORT_ORDER = -1;
  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem(rowsStorageKey);
    return savedRows ? Number(savedRows) : Number(process.env.REACT_APP_LIST_ROW_DEFAULT);
  });

  const LIST_STORAGE_KEY = 'lastProjectListState';
  const DS_STORAGE_KEY = 'lastProjectDetailedSearchForm';

  const savedListState = localStorage.getItem(LIST_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(LIST_STORAGE_KEY))
    : null;

  const savedDetailedForm = localStorage.getItem(DS_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(DS_STORAGE_KEY))
    : null;

  const [detailedSearchQuery, setDetailedSearchQuery] = useState(
    savedListState?.detailedSearchQuery || ''
  );

  const isDetailedSearchActive = React.useMemo(() => {
    return (detailedSearchQuery || '').toString().trim().length > 0;
  }, [detailedSearchQuery]);

  const [dsProjectNumber, setDsProjectNumber] = useState(savedDetailedForm?.project_number || '');
  const [dsPartnerName, setDsPartnerName] = useState(savedDetailedForm?.partner_name || '');
  const [dsContract, setDsContract] = useState(savedDetailedForm?.contract || '');
  const [dsExternalId, setDsExternalId] = useState(savedDetailedForm?.external_id || '');
  const [dsName, setDsName] = useState(savedDetailedForm?.name || '');
  const [dsProjectType, setDsProjectType] = useState(savedDetailedForm?.project_type || null);
  const [dsProjectStatus, setDsProjectStatus] = useState(savedDetailedForm?.project_status || null);
  const [dsDeadlineRange, setDsDeadlineRange] = useState(() => {
    const range = savedDetailedForm?.deadline_range;
    if (!Array.isArray(range)) {
      return null;
    }
    return range.map(date => date ? new Date(date) : null);
  });
  const [dsWarehouseName, setDsWarehouseName] = useState(savedDetailedForm?.warehouse_name || '');
  const [dsLocation, setDsLocation] = useState(savedDetailedForm?.location || null);
  const [dsArchived, setDsArchived] = useState(() => {
    if (savedDetailedForm?.archived !== undefined && savedDetailedForm?.archived !== null) {
      return savedDetailedForm.archived;
    }
    return isSearchView ? false : null;
  });


  const dt = useRef(null);
  // const defaultFilters = {
  //   project_number: { value: null, matchMode: 'contains' },
  //   partner_name: { value: null, matchMode: 'contains' },
  //   name: { value: null, matchMode: 'contains' },
  //   project_type: { value: null, matchMode: 'equals' },
  //   project_status: { value: null, matchMode: 'equals' },
  // };
  const [lazyParams, setLazyParams] = useState(() => {
    const savedState = localStorage.getItem(storageKey);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        return {
          first: parsed.first ?? 0,
          rows: parsed.rows ?? (
            localStorage.getItem(rowsStorageKey)
              ? Number(localStorage.getItem(rowsStorageKey))
              : Number(process.env.REACT_APP_LIST_ROW_DEFAULT)
          ),
          page: parsed.page ?? 0,
          sortField: parsed.sortField ?? DEFAULT_SORT_FIELD,
          sortOrder: typeof parsed.sortOrder === 'number' ? parsed.sortOrder : DEFAULT_SORT_ORDER,
          filters: {},
        };
      } catch (error) {
        console.error('Hibás project list state a localStorage-ban:', error);
      }
    }
    return {
      first: 0,
      rows: localStorage.getItem(rowsStorageKey)
        ? Number(localStorage.getItem(rowsStorageKey))
        : Number(process.env.REACT_APP_LIST_ROW_DEFAULT),
      page: 0,
      sortField: DEFAULT_SORT_FIELD,
      sortOrder: DEFAULT_SORT_ORDER,
      filters: {},
    };
  });
  let loadLazyTimeout = null;
  const loadLazyData = () => {
    setLoading(true);
    if (loadLazyTimeout) {
      clearTimeout(loadLazyTimeout);
    }
    loadLazyTimeout = setTimeout(() => {
      const lazyParamsWithoutFilters = {
      ...lazyParams,
      filters: {}
    };
    let query = window.App.transformQueryParams(lazyParamsWithoutFilters);
    const extras = [];
    if (detailedSearchQuery) {
      extras.push(detailedSearchQuery);
    }
    if (isSearchView && !(detailedSearchQuery || '').includes('search[archived]')) {
      extras.push('search[archived][operator]==&search[archived][value]=false');
    }
    const urlVars = window.App.urlVars();
    if (contractId) {
      extras.push(`search[contract][operator]==&search[contract][value]=${contractId}`);
    }
    if (partnerId) {
      extras.push(`search[partner][operator]==&search[partner][value]=${partnerId}`);
    }
    if (urlVars.from === 'projecttype' && urlVars.project_type) {
      extras.push(`search[project_type][operator]==&search[project_type][value]=${urlVars.project_type}`);
    }
    if (extras.length) query += `${query.includes('?') ? '&' : '?'}${extras.join('&')}`;

    // console.log('[ProjectList] GET:', process.env.REACT_APP_API_HOST + "/api/v2/project" + query);
    
      axios.get(process.env.REACT_APP_API_HOST + "/api/v2/project" + query,
        {
          params: {},
          headers: {
            'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
            'App-Network': localStorage.getItem("networkId")
          }
        })
        .then(response => {
          setTotalRecords(response.data.data.total_item_count);
          setProjects(response.data.data.items)
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
        .finally(() => {
          setLoading(false);
        })
    }, Math.random() * 1000 + 250);
  }
  const onPage = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onSort = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  // const onFilter = (event) => {
  //   let _lazyParams = {...lazyParams, ...event};
  //   _lazyParams['first'] = 0;
  //   _lazyParams['page'] = 0;
  //   setLazyParams(_lazyParams);
  // }
  const pager = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink',
    'RowsPerPageDropdown': (options) => {
      const dropdownOptions = [
        {label: Number(process.env.REACT_APP_LIST_ROW_OP1), value: Number(process.env.REACT_APP_LIST_ROW_OP1)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP2), value: Number(process.env.REACT_APP_LIST_ROW_OP2)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP3), value: Number(process.env.REACT_APP_LIST_ROW_OP3)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP4), value: Number(process.env.REACT_APP_LIST_ROW_OP4)},
      ];

      return (
        <>
          {/*<span className="p-mx-1" style={{color: 'var(--text-color)', userSelect: 'none'}}>Tételek egy oldalon: </span>*/}
          <Dropdown value={options.value} options={dropdownOptions} onChange={(e) => setRows(e.target.value)}
                    appendTo={document.body}/>
        </>
      );
    },
    'CurrentPageReport': (options) => {
      return (
        <span style={{color: 'var(--text-color)', userSelect: 'none', width: '120px', textAlign: 'center'}}>
                        {options.first} - {options.last} / {options.totalRecords}
        </span>
      )
    }
  };

  // const buildFilterQuery = (filters) => {
  //   const parts = [];
  //   Object.entries(filters || {}).forEach(([field, config]) => {
  //     if (!config || config.value === null || config.value === undefined || config.value === '') {
  //       return;
  //     }
  //     if (config.matchMode === 'equals') {
  //       parts.push(`search[${field}][operator]==`);
  //       parts.push(`search[${field}][value]=${encodeURIComponent(config.value)}`);
  //     } else {
  //       parts.push(`search[${field}][operator]=like`);
  //       parts.push(`search[${field}][value]=${encodeURIComponent('*' + config.value + '*')}`);
  //     }
  //   });
  //   return parts.join('&');
  // };

  const persistDetailedForm = () => {
    const payload = {
      project_number: dsProjectNumber,
      partner_name: dsPartnerName,
      contract: dsContract,
      external_id: dsExternalId,
      name: dsName,
      project_type: dsProjectType,
      project_status: dsProjectStatus,
      deadline_range: dsDeadlineRange,
      warehouse_name: dsWarehouseName,
      location: dsLocation,
      archived: dsArchived,
    };
    localStorage.setItem(DS_STORAGE_KEY, JSON.stringify(payload));
  };

  const clearDetailedFormPersist = () => {
    localStorage.removeItem(DS_STORAGE_KEY);
  };

  const buildDetailedSearchQuery = () => {
    const parts = [];
    const like = (field, val) => {
      const v = (val || '').toString().trim();
      if (!v) return;
      parts.push(`search[${field}][operator]=like&search[${field}][value]=*${encodeURIComponent(v)}*`);
    };
    const equals = (field, val) => {
      if (val === null || val === undefined || val === '') return;

      parts.push(`search[${field}][operator]==&search[${field}][value]=${encodeURIComponent(val)}`);
    };
    const dateRange = (field, range) => {
      if (!Array.isArray(range)) return;
      const from = range[0] ? moment(range[0]).format('YYYY-MM-DD 00:00:00') : null;
      const to = range[1] ? moment(range[1]).format('YYYY-MM-DD 23:59:59') : null;

      if (from && to) {
        parts.push(`search[${field}][operator]=between&search[${field}][value]=${encodeURIComponent(from + ',' + to)}`);
      } else if (from) {
        parts.push(`search[${field}][operator]=>=&search[${field}][value]=${encodeURIComponent(from)}`);
      } else if (to) {
        parts.push(`search[${field}][operator]=<=&search[${field}][value]=${encodeURIComponent(to)}`);
      }
    };
    like('project_number', dsProjectNumber);
    if (!isAllProjectsView && !hidePartnerColumn && typeConfig?.value_partner !== 'disabled') {
      like('partner_name', dsPartnerName);
    }
    if (!isAllProjectsView && typeConfig?.value_contract !== 'disabled') {
      like('contract', dsContract);
    }
    if (!isAllProjectsView && typeConfig?.value_external_id !== 'disabled') {
      like('external_id', dsExternalId);
    }
    like('name', dsName);
    if (isAllProjectsView || !window.App.urlVars().project_type) {
      equals('project_type', dsProjectType);
    }
    equals('project_status', dsProjectStatus);
    if (!isAllProjectsView && typeConfig?.value_deadline !== 'disabled') {
      dateRange('deadline', dsDeadlineRange);
    }
    if (!isAllProjectsView && typeConfig?.warehouse_mode !== 'NONE') {
      like('warehouse_name', dsWarehouseName);
    }
    if (!isAllProjectsView && typeConfig?.value_location !== 'disabled') {
      equals('location', dsLocation);
    }
    equals('archived', dsArchived);
    return parts.filter(Boolean).join('&');
  };

  const doDetailedSearch = () => {
    const q = buildDetailedSearchQuery();
    setDetailedSearchQuery(q);
    persistDetailedForm();
    setLazyParams((prev) => ({
      ...prev,
      first: 0,
      page: 0,
    }));
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        {localStorage.getItem('roles').toString().includes('role_project_write') &&
          <Button icon="pi pi-pencil" className="  p-button-warning p-mr-2"
                  tooltip={t('edit')} tooltipOptions={{position: 'left'}}
                  onClick={() => {
                    const currentSearch = history.location.search || '';
                    const currentReturn = '/project' + currentSearch;
                    history.push( '/project' + currentSearch + (currentSearch ? '&' : '?') + 'update=project' + '&projectId=' + rowData.id +
                     '&projectDtoId=' + rowData.shortcut_dto_id + '&return=' + encodeURIComponent(currentReturn)
                    );
                  }}/>
        }
        <Button icon="pi pi-eye" className=" p-button p-mr-2"
                tooltip={t('view')} tooltipOptions={{position: 'left'}}
                onClick={() => {
                  localStorage.setItem('breadcrumb', JSON.stringify([
                      {label: t('projects'), url: '/#/project'},
                      {label: rowData.name, url: '/#/project/project/read/' + rowData.id},
                    ])
                  )
                  history.push('/project/project/read/' + rowData.id)
                }}/>
        {/*<Button icon="pi pi-file" className="  p-button-warning p-mr-2"*/}
        {/*        tooltip="Munkalap" tooltipOptions={{position: 'left'}}*/}
        {/*        onClick={() => {*/}
        {/*          history.push('/project/worksheet/update/' + rowData.shortcut_dto_id)*/}
        {/*        }}/>*/}
      </React.Fragment>
    );
  }

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
  }

  const statusFilterTemplate = (options) => (
    <Dropdown
      value={options.value}
      options={projectStatuses}
      optionLabel="name"
      optionValue="id"
      placeholder={t('dsAll')}
      onChange={(e) => options.filterApplyCallback(e.value)}
      appendTo={document.body}
    />
  );

  const typeFilterTemplate = (options) => (
    <Dropdown
      value={options.value}
      options={projectTypes}
      optionLabel="name"
      optionValue="id"
      placeholder={t('dsAll')}
      onChange={(e) => options.filterApplyCallback(e.value)}
      appendTo={document.body}
    />
  );

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({
      ...lazyParams,
      filters: {},
      detailedSearchQuery,
      // sortField: lazyParams.sortField || DEFAULT_SORT_FIELD,
      // sortOrder: typeof lazyParams.sortOrder === 'number' ? lazyParams.sortOrder : DEFAULT_SORT_ORDER,
    }));
    localStorage.setItem(LIST_STORAGE_KEY, JSON.stringify({
      rows,
      lazyParams: {
        ...lazyParams,
        filters: {},
      },
      detailedSearchQuery,
      // sortField: lazyParams.sortField || DEFAULT_SORT_FIELD,
      // sortOrder: typeof lazyParams.sortOrder === 'number' ? lazyParams.sortOrder : DEFAULT_SORT_ORDER,
    }));
  }, [lazyParams, storageKey, detailedSearchQuery, rows]);

  useEffect(() => {
    if (typeof onDetailedSearchActiveChange === 'function') {
      onDetailedSearchActiveChange(isDetailedSearchActive);
    }
  }, [isDetailedSearchActive, onDetailedSearchActiveChange]);

  useEffect(() => {
    const typeId = urlVars.project_type;
    if (typeId && !isAllProjectsView) {
      axios.get(process.env.REACT_APP_API_HOST + "/api/v2/project-type/" + typeId, {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem("userToken"), 'App-Network': localStorage.getItem("networkId") }
      }).then(res => {
        setTypeConfig(res.data.data);
      }).catch(() => setTypeConfig(null));
    } else {
      setTypeConfig(null);
    }
  }, [urlVars.project_type, isAllProjectsView]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({
      ...lazyParams,
      sortField: lazyParams.sortField || DEFAULT_SORT_FIELD,
      sortOrder: typeof lazyParams.sortOrder === 'number' ? lazyParams.sortOrder : DEFAULT_SORT_ORDER,
    }));
  }, [lazyParams, storageKey]);

  useEffect(() => {
    localStorage.setItem(rowsStorageKey, String(rows));
    setLazyParams((prev) => ({
      ...prev,
      rows: rows
    }));
  }, [rows, rowsStorageKey]);

  useEffect(() => {
    loadLazyData();
    apiCall('get', '/api/v2/project-status?' + JSON.stringify({
      first: 0,
      rows: 9999,
      page: 0,
    }))
      .then(response => {
        setProjectStatuses(response.data.data.items);
      })
      .catch(error => {
        window.App.toastShow('error', 'Hiba történt!', error.toString(), '', error.response);
      })
    apiCall('get', '/api/v2/project-type?' + JSON.stringify({
      first: 0,
      rows: 9999,
      page: 0,
    }))
      .then(response => {
        setProjectTypes(response.data.data.items);
      })
      .catch(error => {
        window.App.toastShow('error', 'Hiba történt!', error.toString(), '', error.response);
      });
     apiCall('get', '/api/v2/location?item_per_page=9999')
      .then(response => {
        setLocations(response.data.data.items || []);
      })
      .catch(error => {
        window.App.toastShow('error', 'Hiba történt!', error.toString(), '', error.response);
      });
  }, [lazyParams, detailedSearchQuery, contractId, partnerId, window.App.state.rerenderProjectList, listContextKey]);

  return (
    <>
      {isSearchView && (
        <div className="p-text-right p-mb-2">
          <Button
            type="button"
            label={t('detailedSearch')}
            icon={isDetailedSearchActive ? "pi pi-filter-fill" : "pi pi-filter"}
            className="p-button-outlined"
            onClick={() => setLocalDetailedSearchVisible(v => !v)}
          />
        </div>
      )}

      {isDetailedSearchOpen && (
        <Panel
          className="p-mb-2"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              doDetailedSearch();
            }
          }}
        >
          <div className="p-grid">
            <div className="p-col-12 p-lg-3">
              <div className="p-field p-fluid">
                <label>{t('projectNumber')}</label>
                <InputText
                  value={dsProjectNumber}
                  onChange={(e) => setDsProjectNumber(e.target.value)}
                />
              </div>
            </div>

            {!isAllProjectsView && !hidePartnerColumn && typeConfig?.value_partner !== 'disabled' && (
              <div className="p-col-12 p-lg-3">
                <div className="p-field p-fluid">
                  <label>{t('columnPartner')}</label>
                  <InputText
                    value={dsPartnerName}
                    onChange={(e) => setDsPartnerName(e.target.value)}
                  />
                </div>
              </div>
            )}

            {!isAllProjectsView && typeConfig?.value_contract !== 'disabled' && (
              <div className="p-col-12 p-lg-3">
                <div className="p-field p-fluid">
                  <label>{t('columnContract')}</label>
                  <InputText
                    value={dsContract}
                    onChange={(e) => setDsContract(e.target.value)}
                  />
                </div>
              </div>
            )}

            {!isAllProjectsView && typeConfig?.value_external_id !== 'disabled' && (
              <div className="p-col-12 p-lg-3">
                <div className="p-field p-fluid">
                  <label>{t('externalReference')}</label>
                  <InputText
                    value={dsExternalId}
                    onChange={(e) => setDsExternalId(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="p-col-12 p-lg-3">
              <div className="p-field p-fluid">
                <label>{t('columnName')}</label>
                <InputText
                  value={dsName}
                  onChange={(e) => setDsName(e.target.value)}
                />
              </div>
            </div>

            {(isAllProjectsView || !window.App.urlVars().project_type) && (
              <div className="p-col-12 p-lg-3">
                <div className="p-field p-fluid">
                  <label>{t('columnType')}</label>
                  <Dropdown
                    value={dsProjectType}
                    options={projectTypes}
                    optionLabel="name"
                    optionValue="id"
                    onChange={(e) => setDsProjectType(e.value)}
                    showClear
                    placeholder={t('dsSelect')}
                    appendTo={document.body}
                  />
                </div>
              </div>
            )}

            <div className="p-col-12 p-lg-3">
              <div className="p-field p-fluid">
                <label>{t('columnStatus2')}</label>
                <Dropdown
                  value={dsProjectStatus}
                  options={projectStatuses}
                  optionLabel="name"
                  optionValue="id"
                  onChange={(e) => setDsProjectStatus(e.value)}
                  showClear
                  placeholder={t('dsSelect')}
                  appendTo={document.body}
                />
              </div>
            </div>

            <div className="p-col-12 p-lg-3">
              <div className="p-field p-fluid">
                <label>{t('columnArchived')}</label>
                <div className="p-d-flex p-ai-center" style={{ height: '38px' }}>
                  <TriStateCheckbox
                    value={dsArchived}
                    onChange={(e) => setDsArchived(e.value)}
                  />
                  <span className="p-ml-2">
                    {dsArchived === true ? props.t('yes') : dsArchived === false ? props.t('no') : props.t('whatever')}
                  </span>
                </div>
              </div>
            </div>

            {!isAllProjectsView && typeConfig?.value_deadline !== 'disabled' && (
              <div className="p-col-12 p-lg-3">
                <div className="p-field p-fluid">
                  <label>{t('columnDeadline')}</label>
                  <Calendar
                    value={dsDeadlineRange}
                    onChange={(e) => setDsDeadlineRange(e.value)}
                    selectionMode="range"
                    readOnlyInput
                    showIcon
                    dateFormat="yy-mm-dd"
                    appendTo={document.body}
                  />
                </div>
              </div>
            )}

            {!isAllProjectsView && typeConfig?.warehouse_mode !== 'NONE' && (
              <div className="p-col-12 p-lg-3">
                <div className="p-field p-fluid">
                  <label>{t('columnWarehouse')}</label>
                  <InputText
                    value={dsWarehouseName}
                    onChange={(e) => setDsWarehouseName(e.target.value)}
                  />
                </div>
              </div>
            )}

            {!isAllProjectsView && typeConfig?.value_location !== 'disabled' && (
              <div className="p-col-12 p-lg-3">
                <div className="p-field p-fluid">
                  <label>{t('columnLocation')}</label>
                  <Dropdown
                    value={dsLocation}
                    options={locations}
                    optionLabel="name"
                    optionValue="id"
                    onChange={(e) => setDsLocation(e.value)}
                    showClear
                    placeholder={t('dsSelect')}
                    appendTo={document.body}
                  />
                </div>
              </div>
            )}

            <div className="p-col-12 p-text-right">
              <Button
                type="button"
                label={t('dsClearCriteria')}
                icon="pi pi-times"
                className="p-button-danger p-mr-2"
                onClick={() => {
                  setDsProjectNumber('');
                  setDsPartnerName('');
                  setDsContract('');
                  setDsExternalId('');
                  setDsName('');
                  setDsProjectType(null);
                  setDsProjectStatus(null);
                  setDsDeadlineRange(null);
                  setDsWarehouseName('');
                  setDsLocation(null);
                  setDetailedSearchQuery('');
                  setDsArchived(isSearchView ? false : null);
                  clearDetailedFormPersist();
                  localStorage.removeItem(LIST_STORAGE_KEY);
                  localStorage.removeItem(storageKey);

                  setLazyParams({
                    first: 0,
                    rows: rows,
                    page: 0,
                    sortField: DEFAULT_SORT_FIELD,
                    sortOrder: DEFAULT_SORT_ORDER,
                    filters: {},
                  });
                }}
              />

              <Button
                type="button"
                label={t('dsSearch')}
                icon="pi pi-search"
                className="p-button-primary"
                onClick={doDetailedSearch}
              />
            </div>
          </div>
        </Panel>
      )}

      <DataTable size={localStorage.getItem('scale')}
                 emptyMessage={t('noResults')}
                 value={projects} paginator first={lazyParams.first} totalRecords={totalRecords} onPage={onPage}
                 onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                 loading={loading} lazy
                 paginatorTemplate={pager} rows={lazyParams.rows}
                 autoLayout={true}
                 responsiveLayout="scroll"
                 stripedRows
                 ref={dt}
                 selectionMode={"single"}
                 dataKey="id"
                 onRowDoubleClick={(rowData) => {
                  if (window.App.urlVars().search === 'project') {
                    const currentVars = window.App.urlVars();
                    const cleanedVars = { ...currentVars };
                    delete cleanedVars.search;
                    delete cleanedVars.project;
                    delete cleanedVars.projectId;
                    delete cleanedVars.projectName;
                    cleanedVars.projectId = rowData.data.id;
                    cleanedVars.projectName = encodeURIComponent(rowData.data.name || '');
                    let query = '?';
                    Object.entries(cleanedVars).forEach(([key, value]) => {
                      if (value !== undefined && value !== null) {
                        query += key + '=' + value + '&';
                      }
                    });
                    history.push(props.backlink + query);
                  }
                }}
      >
        <Column style={{position: 'relative', width: '10px', maxWidth: '10px'}}
                body={(rowData) => {
                  if (rowData && rowData.project_status) {
                    let status = projectStatuses.filter(item => item.id === rowData.project_status.id)
                    return (
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        top: '0',
                        height: '100%',
                        borderLeft: '10px solid ' + status[0].color,
                      }}></span>
                    )
                  }
                }}/>
        <Column field="project_number" header={t('projectNumber')} bodyClassName="col-body-font-bold" sortable></Column>
        {!isAllProjectsView && !hidePartnerColumn && typeConfig?.value_partner !== 'disabled' && (
          <Column field="partner.name" header={t('columnPartner')} sortable filterField='partner_name' />
        )}
        {!isAllProjectsView && typeConfig?.value_contract !== 'disabled' && (
          <Column field="contract" header={t('columnContract')} bodyClassName="col-body-font-bold" sortable body={(rowData) => {
            const contract = rowData?.contract;
            if (!contract) {
              return '-';
            }
            if (typeof contract === 'object') {
              return (
                contract.name ||
                contract.contract_number ||
                contract.number ||
                contract.id ||
                '-'
              );
            }
            return contract;
          }}></Column>
        )}
        {!isAllProjectsView && typeConfig?.value_external_id !== 'disabled' && (
          <Column field="external_id" header={t('columnExternalIdentifier')} bodyClassName="col-body-font-bold" sortable></Column>
        )}
        <Column field="name" header={t('columnName')} bodyClassName="col-body-font-bold" sortable></Column>
        <Column field="project_type.name" header={t('columnType')} filterField='project_type' filterMatchMode="equals"  filterElement={typeFilterTemplate}></Column>
        <Column field="project_status.name" header={t('columnStatus2')} sortable filterField="project_status" filterMatchMode="equals" filterElement={statusFilterTemplate}></Column>
        {!isAllProjectsView && typeConfig?.continuous_fulfillment === false && (
          <Column field="net_amount" header={t('columnNetValue')} sortable className="p-text-right"
                  body={(rowData) => window.App.currencyFormatter(rowData?.currency || 'HUF', rowData.net_amount)}></Column>
        )}
        {!isAllProjectsView && typeConfig?.value_deadline !== 'disabled' && (
        <Column field="deadline" header={t('deadline')} sortable className={"p-text-nowrap"} body={(rowData) => {
          if (rowData.deadline && rowData.deadline !== null) {
            return (
              <>{window.App.formatDateValue(rowData.deadline)}</>
            )
          }
        }}></Column>
        )}
        {!isAllProjectsView && typeConfig?.warehouse_mode !== 'NONE' && (
          <Column field="warehouse.name" header={t('columnWarehouse')} bodyClassName="col-body-font-bold" sortable></Column>
        )}
        {!isAllProjectsView && typeConfig?.value_location !== 'disabled' && (
          <Column field="location.name" header={t('columnLocation')} bodyClassName="col-body-font-bold" sortable></Column>
        )}

        <Column field="created_at" header={t('columnCreatedAt')} sortable body={(rowData) => {
          return (
            <>{window.App.formatDateValue(rowData.created_at)}</>
          )
        }}></Column>
        {!isAllProjectsView && (
          <Column field="created_by_name" header={t('columnCreatedBy')} sortable></Column>
        )}
        {!isAllProjectsView && (
        <Column field="has_subcontractor" header={t('columnSubcontractor')} sortable body={(rowData) => {
          return (
            <>
              {rowData.has_subcontractor === true ? (
                <i className={"pi pi-check text-success"}></i>
              ) : (
                <i className={"pi pi-times text-danger"}></i>
              )}
            </>
          )
        }}></Column>
        )}
        {props.view !== 'search' && (
        <Column body={actionBodyTemplate} className={"p-py-0 p-text-right p-text-nowrap"}></Column>
        )}
      </DataTable>
    </>
  )
}
export default withTranslation('common')(ProjectListComponent);
